import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { ArrowDownLeft, ArrowUpRight, ChevronDown } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { formatDateTime } from '@/lib/dates'
import { cn } from '@/lib/utils'
import { useTicketEmails } from '@/queries/emailLog'
import type { EmailLogEntry } from '@/types/emailLog'
import { emailStatusLabels, emailStatusVariants, kindLabel, openedByLabel, readSummary, recipientSummary, statusBreakdown } from './emailStatus'
import { EmailDetailDialog } from './EmailDetailDialog'

function EmailLine({ entry, onOpen }: { entry: EmailLogEntry; onOpen: (id: string) => void }) {
  const [expanded, setExpanded] = useState(false)
  const Icon = entry.direction === 'outbound' ? ArrowUpRight : ArrowDownLeft
  const many = entry.recipientCount > 1
  const breakdown = statusBreakdown(entry)
  const opens = entry.direction === 'outbound'
    ? many ? openedByLabel(entry) : entry.recipients[0] && readSummary(entry.recipients[0])
    : null

  return (
    <li data-testid="ticket-email" className="rounded-etn-card border border-etn-border-hairline bg-etn-surface">
      <button type="button" onClick={() => onOpen(entry._id)} className="block w-full space-y-1 px-3 pb-2 pt-2.5 text-left">
        <span data-testid="email-line-kind" className="flex items-center gap-2 text-etn-control">
          <Icon className="h-3.5 w-3.5 shrink-0 text-etn-text-soft" aria-label={entry.direction === 'outbound' ? 'Outbound' : 'Inbound'} />
          <span className="min-w-0 flex-1 font-semibold text-etn-text-cell">{kindLabel(entry)}</span>
          <span className="shrink-0 text-etn-micro text-etn-text-label">{formatDateTime(entry.createdAt)}</span>
        </span>
        <span data-testid="email-line-recipients" className="flex items-center gap-2 text-etn-micro">
          <span className="min-w-0 flex-1 break-words text-etn-text-label">{recipientSummary(entry)}</span>
          <Badge variant={emailStatusVariants[entry.status]} title={breakdown ?? undefined}>
            {emailStatusLabels[entry.status]}
          </Badge>
        </span>
        {opens && (
          <span data-testid="email-line-opens" className="block text-etn-micro text-etn-success-text">{opens}</span>
        )}
      </button>
      {many && (
        <div className="border-t border-etn-border-hairline px-3 py-1.5">
          <button
            type="button"
            aria-expanded={expanded}
            aria-label={expanded ? 'Hide recipients' : 'Show recipients'}
            onClick={() => setExpanded(v => !v)}
            className="inline-flex items-center gap-1 text-etn-micro font-semibold text-etn-text-label hover:text-etn-text"
          >
            {expanded ? 'Hide recipients' : 'Recipients'}
            <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', expanded && 'rotate-180')} />
          </button>
          {expanded && (
            <ul className="mt-2 space-y-2.5 pb-1" aria-label="Recipients">
              {entry.recipients.map(r => (
                <li key={r._id} className="text-etn-micro">
                  <span className="flex items-start gap-2">
                    <span className="min-w-0 flex-1 break-all text-etn-text-cell">{r.address}</span>
                    <Badge variant={emailStatusVariants[r.status]} className="shrink-0">{emailStatusLabels[r.status]}</Badge>
                  </span>
                  {readSummary(r) && <span className="mt-0.5 block text-etn-text-label">{readSummary(r)}</span>}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </li>
  )
}

// Body of the staff rail's "Emails" disclosure: one line per email (a pool
// notification is one line, expandable to its recipients). Fetches only once
// the disclosure has been opened: most visits to a ticket never look.
export function TicketEmails({ ticketId, enabled }: { ticketId: string; enabled: boolean }) {
  const { data, isLoading, error } = useTicketEmails(ticketId, enabled)
  const [openId, setOpenId] = useState<string | undefined>()

  if (isLoading) return <p className="text-etn-micro text-etn-text-soft">Loading…</p>
  if (error) return <p className="text-etn-micro text-etn-danger-text">Couldn't load emails.</p>
  const rows = data?.data ?? []
  if (!rows.length) return <p className="text-etn-micro text-etn-text-soft">No emails for this ticket yet.</p>

  return (
    <div className="space-y-3">
      <ul className="space-y-2">
        {rows.map(entry => (
          <EmailLine key={entry._id} entry={entry} onOpen={setOpenId} />
        ))}
      </ul>
      <Link
        to={'/admin/email-log' as never}
        search={{ ticketId } as never}
        className="inline-block text-etn-micro font-semibold text-etn-accent-text-active hover:underline"
      >
        Open in email log
      </Link>
      <EmailDetailDialog id={openId} onClose={() => setOpenId(undefined)} />
    </div>
  )
}
